import {uuid} from "@/interfaces";
import {api} from "@/lib/api";
import type {UserMini} from "@/lib/store/users.slice.ts";
import {sendersOf, toChatMessage} from "@/repositories/chat-records.ts";
import {ChatRoomMessage} from "@/models/chat.model.ts";
import type {ChatMessageRequest} from "@shyne/shared/api";

export interface MessagePage {
    messages: ChatRoomMessage[];
    senders: UserMini[];
    hasOlder: boolean;
}

export const FILE_UPLOAD_REFUSED = "The file could not be uploaded. Please try again.";

const contentTypeOf = (file: File) => file.type || "application/octet-stream";

export const ChatMessagesRepository = {
    async page(roomId: uuid, before: uuid | null): Promise<MessagePage> {
        const page = await api.chatRooms.messages(roomId, before === null ? {} : {before});
        const oldestFirst = [...page.items].reverse();

        return {
            messages: oldestFirst.map(toChatMessage),
            senders: sendersOf(oldestFirst),
            hasOlder: page.hasOlder,
        };
    },

    async send(roomId: uuid, request: ChatMessageRequest): Promise<ChatRoomMessage> {
        return toChatMessage(await api.chatRooms.send(roomId, request));
    },

    async uploadFile(roomId: uuid, file: File, put: typeof fetch = fetch): Promise<string> {
        const contentType = contentTypeOf(file);
        const { uploadUrl, fileUrl } = await api.chatRooms.fileUpload(roomId, { name: file.name, contentType });
        const answer = await put(uploadUrl, { method: "PUT", headers: { "content-type": contentType }, body: file });
        if (!answer.ok) throw new Error(FILE_UPLOAD_REFUSED);
        return fileUrl;
    },

    async markProcessed(roomId: uuid, messageId: uuid): Promise<ChatRoomMessage> {
        return toChatMessage(await api.chatRooms.markProcessed(roomId, messageId));
    },
}
